import type { ITabbedProductCards } from '@models/ITabbedProductCards';
import NavigationCarousel from '@molecules/NavigationCarousel/NavigationCarousel';
import PaginationCarousel from '@molecules/PaginationCarousel/PaginationCarousel';
import TabbedContentSection from '@molecules/TabbedContentSection/TabbedContentSection';
import updateDataLayer from '@utils/helpers/gtm';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import { useState } from 'react';
import { SwiperSlide } from 'swiper/react';
import TabbedProductCard from './TabbedProductCard';

const TabbedProductCards = ({
  anchorId = '',
  trumpetText,
  title,
  subtitle,
  tabs,
}: ITabbedProductCards) => {
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const activeTabCards = tabs?.[activeTabIndex]?.cards ?? [];
  const dataTestId = 'CT-183-Tabbed-Product-Cards';
  const isMobile = useMediaQuery(768);

  const handleTabChange = (index: number) => {
    if (index !== activeTabIndex) {
      setActiveTabIndex(index);

      updateDataLayer({
        event: 'selectContent',
        content_type: 'category_tabs',
        link_text: tabs?.[index]?.title,
      });
    }
  };

  const mobileCarousel = (
    <PaginationCarousel
      key={activeTabIndex}
      id={`${dataTestId}-mobile-carousel`}
      slidesPerView={activeTabCards.length > 1 ? 1.2 : 1}
      spaceBetween={20}
      paginationBulletsVisible={activeTabCards.length > 1}
      paginationClasses="mt-10"
      paginationContainerClasses="!gap-5"
      paginationBulletClasses="!w-[15px] !h-[15px]"
    >
      {activeTabCards.map((card) => (
        <SwiperSlide className="stretch-card" key={card.url}>
          <TabbedProductCard {...card} />
        </SwiperSlide>
      ))}
    </PaginationCarousel>
  );

  const desktopCarousel = (
    <NavigationCarousel
      key={activeTabIndex}
      id={`${dataTestId}-carousel-${activeTabIndex}`}
      items={activeTabCards}
      slidesToShowTablet={2.2}
      slidesToShowDesktop={4}
      additionalBreakpoints={{ 1024: { slidesPerView: 2.2 } }}
      spaceBetween={16}
      carouselHorizontalSpacingClassName=" "
    >
      {(items) =>
        items.map(({ ariaHidden, ariaLabel, inert, ...card }) => (
          <SwiperSlide
            className="stretch-card"
            key={card.productName}
            aria-hidden={ariaHidden}
            aria-label={ariaLabel}
            inert={inert}
          >
            <TabbedProductCard {...card} />
          </SwiperSlide>
        ))
      }
    </NavigationCarousel>
  );

  return (
    <TabbedContentSection
      anchorId={anchorId}
      dataTestId={dataTestId}
      desktopContent={!isMobile ? { content: desktopCarousel } : undefined}
      mobileContent={isMobile ? mobileCarousel : desktopCarousel}
      onTabChange={handleTabChange}
      showContent={activeTabCards.length > 0}
      subtitle={subtitle}
      tabs={tabs}
      title={title}
      trumpetText={trumpetText}
    />
  );
};

export default TabbedProductCards;
